/**
 * AI Learning Playground - Utility Functions
 * 
 * Common utilities used across the application
 */

import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { nanoid } from 'nanoid';

// ============================================
// Class Name Utilities
// ============================================

/**
 * Merge Tailwind CSS classes with clsx
 */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

// ============================================
// ID Generation
// ============================================

/**
 * Generate a unique ID with optional prefix
 */
export function generateId(prefix?: string): string {
  const id = nanoid(10);
  return prefix ? `${prefix}_${id}` : id;
}

// ============================================
// String Utilities
// ============================================

/**
 * Truncate text to specified length with ellipsis
 */
export function truncate(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength - 3)}...`;
}

/**
 * Capitalize first letter of string
 */
export function capitalize(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/**
 * Convert camelCase to Title Case
 */
export function camelToTitle(text: string): string {
  return text
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (str) => str.toUpperCase())
    .trim();
}

/**
 * Slugify text for URLs
 */
export function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

// ============================================
// Number Utilities
// ============================================

/**
 * Format number with comma separators
 */
export function formatNumber(num: number): string {
  return new Intl.NumberFormat('en-US').format(num);
}

/**
 * Format bytes to human-readable string
 */
export function formatBytes(bytes: number, decimals = 2): string {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(decimals))} ${sizes[i]}`;
}

/**
 * Format duration in milliseconds to human-readable string
 */
export function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(2)}s`;
  const minutes = Math.floor(ms / 60000);
  const seconds = ((ms % 60000) / 1000).toFixed(0);
  return `${minutes}m ${seconds}s`;
}

/**
 * Round to specified decimal places
 */
export function round(num: number, decimals = 2): number {
  return Math.round(num * Math.pow(10, decimals)) / Math.pow(10, decimals);
}

/**
 * Clamp value between min and max
 */
export function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

/**
 * Calculate percentage
 */
export function percentage(value: number, total: number): number {
  if (total === 0) return 0;
  return round((value / total) * 100);
}

// ============================================
// Array Utilities
// ============================================

/**
 * Group array items by key
 */
export function groupBy<T>(array: T[], key: keyof T): Record<string, T[]> {
  return array.reduce((groups, item) => {
    const groupKey = String(item[key]);
    if (!groups[groupKey]) {
      groups[groupKey] = [];
    }
    groups[groupKey].push(item);
    return groups;
  }, {} as Record<string, T[]>);
}

/**
 * Remove duplicates from array
 */
export function unique<T>(array: T[], key?: keyof T): T[] {
  if (key) {
    const seen = new Set();
    return array.filter((item) => {
      const value = item[key];
      if (seen.has(value)) return false;
      seen.add(value);
      return true;
    });
  }
  return [...new Set(array)];
}

/**
 * Chunk array into smaller arrays
 */
export function chunk<T>(array: T[], size: number): T[][] {
  const chunks: T[][] = [];
  for (let i = 0; i < array.length; i += size) {
    chunks.push(array.slice(i, i + size));
  }
  return chunks;
}

/**
 * Shuffle array (Fisher-Yates)
 */
export function shuffle<T>(array: T[]): T[] {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

// ============================================
// Object Utilities
// ============================================

/**
 * Deep clone an object
 */
export function deepClone<T>(obj: T): T {
  return JSON.parse(JSON.stringify(obj));
}

/**
 * Check if object is empty
 */
export function isEmpty(obj: object): boolean {
  return Object.keys(obj).length === 0;
}

/**
 * Pick specific keys from object
 */
export function pick<T extends object, K extends keyof T>(
  obj: T,
  keys: K[]
): Pick<T, K> {
  const result = {} as Pick<T, K>;
  keys.forEach((key) => {
    if (key in obj) {
      result[key] = obj[key];
    }
  });
  return result;
}

/**
 * Omit specific keys from object
 */
export function omit<T extends object, K extends keyof T>(
  obj: T,
  keys: K[]
): Omit<T, K> {
  const result = { ...obj };
  keys.forEach((key) => {
    delete result[key];
  });
  return result as Omit<T, K>;
}

// ============================================
// Date Utilities
// ============================================

/**
 * Format date to relative time string
 */
export function formatRelativeTime(date: Date | number): string {
  const now = new Date();
  const target = typeof date === 'number' ? new Date(date) : date;
  const diffMs = now.getTime() - target.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);

  if (diffSec < 60) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHour < 24) return `${diffHour}h ago`;
  if (diffDay < 7) return `${diffDay}d ago`;
  
  return target.toLocaleDateString();
}

/**
 * Format date to ISO string without milliseconds
 */
export function formatISODate(date: Date): string {
  return date.toISOString().split('.')[0] + 'Z';
}

// ============================================
// Async Utilities
// ============================================

/**
 * Sleep for specified milliseconds
 */
export function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Debounce function
 */
export function debounce<T extends (...args: unknown[]) => unknown>(
  fn: T,
  delay: number
): (...args: Parameters<T>) => void {
  let timeoutId: ReturnType<typeof setTimeout>;
  return (...args: Parameters<T>) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), delay);
  };
}

/**
 * Throttle function
 */
export function throttle<T extends (...args: unknown[]) => unknown>(
  fn: T,
  limit: number
): (...args: Parameters<T>) => void {
  let inThrottle: boolean;
  return (...args: Parameters<T>) => {
    if (!inThrottle) {
      fn(...args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
}

/**
 * Retry function with exponential backoff
 */
export async function retry<T>(
  fn: () => Promise<T>,
  maxAttempts = 3,
  baseDelay = 1000
): Promise<T> {
  let lastError: Error;
  
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error as Error;
      if (attempt < maxAttempts - 1) {
        await sleep(baseDelay * Math.pow(2, attempt));
      }
    }
  }
  
  throw lastError!;
}

// ============================================
// Text Processing Utilities
// ============================================

import { getEncoding, type Tiktoken } from 'js-tiktoken';
import type { TokenizerModel } from '@/types';

// Cache encodings for performance
const encodingCache = new Map<string, Tiktoken>();

// Valid tiktoken encoding names
type TiktokenEncodingName = 'cl100k_base' | 'p50k_base' | 'r50k_base' | 'p50k_edit';

/**
 * Get tiktoken encoding for a model
 */
function getEncodingForModel(model: TokenizerModel): Tiktoken {
  // Map models to their tiktoken encodings
  // OpenAI models use accurate tiktoken, others use cl100k_base as approximation
  const modelToEncoding: Record<TokenizerModel, TiktokenEncodingName> = {
    // OpenAI (accurate)
    'gpt-4': 'cl100k_base',
    'gpt-4-turbo': 'cl100k_base',
    'gpt-4o': 'cl100k_base',
    'gpt-4o-mini': 'cl100k_base',
    'gpt-3.5-turbo': 'cl100k_base',
    // Anthropic Claude (approximation)
    'claude-3-opus': 'cl100k_base',
    'claude-3-sonnet': 'cl100k_base',
    'claude-3-haiku': 'cl100k_base',
    'claude-3.5-sonnet': 'cl100k_base',
    // Google Gemini (approximation)
    'gemini-pro': 'cl100k_base',
    'gemini-1.5-pro': 'cl100k_base',
    'gemini-1.5-flash': 'cl100k_base',
    'gemini-ultra': 'cl100k_base',
    // DeepSeek (approximation)
    'deepseek-chat': 'cl100k_base',
    'deepseek-coder': 'cl100k_base',
    // Qwen (approximation)
    'qwen-turbo': 'cl100k_base',
    'qwen-plus': 'cl100k_base',
    'qwen-max': 'cl100k_base',
    // Other models (approximation)
    'llama-3': 'cl100k_base',
    'llama-3.1': 'cl100k_base',
    'mistral-large': 'cl100k_base',
    'mixtral': 'cl100k_base',
  };

  const encodingName = modelToEncoding[model];
  
  // Check cache first
  if (encodingCache.has(encodingName)) {
    return encodingCache.get(encodingName)!;
  }

  // Get encoding and cache it
  try {
    const encoding = getEncoding(encodingName);
    encodingCache.set(encodingName, encoding);
    return encoding;
  } catch (error) {
    // Fallback to cl100k_base if model not found
    console.warn(`Encoding not found for model ${model}, using cl100k_base as fallback`);
    const fallback = getEncoding('cl100k_base');
    encodingCache.set('cl100k_base', fallback);
    return fallback;
  }
}

/**
 * Count tokens using tiktoken (accurate for OpenAI models)
 * Uses approximation for other models (no official JS tokenizer available)
 */
export function countTokens(text: string, model: TokenizerModel = 'gpt-4'): number {
  if (!text || text.length === 0) {
    return 0;
  }

  // For all models, use tiktoken encoding (accurate for OpenAI, approximation for others)
  try {
    const encoding = getEncodingForModel(model);
    return encoding.encode(text).length;
  } catch (error) {
    console.error('Error counting tokens with tiktoken:', error);
    // Fallback to approximation
    return Math.ceil(text.length / 4);
  }
}

/**
 * Get token boundaries (start and end character positions) for highlighting
 * Returns array of {start, end} positions for each token
 * 
 * Note: This is an approximation. For perfect accuracy, we'd need to track
 * token positions during encoding, which tiktoken doesn't provide directly.
 */
export function getTokenBoundaries(text: string, model: TokenizerModel = 'gpt-4'): Array<{ start: number; end: number }> {
  if (!text || text.length === 0) {
    return [];
  }

  try {
    const encoding = getEncodingForModel(model);
    const tokenIds = encoding.encode(text);
    const boundaries: Array<{ start: number; end: number }> = [];
    let currentPos = 0;

    // Decode each token and find its position in the original text sequentially
    for (const tokenId of tokenIds) {
      const tokenText = encoding.decode([tokenId]);
      
      // Find the token text starting from currentPos to maintain order
      const tokenStart = text.indexOf(tokenText, currentPos);
      
      if (tokenStart === -1) {
        // If not found from currentPos, the tokenization might have issues
        // Try to find it from the beginning as fallback
        const fallbackStart = text.indexOf(tokenText, 0);
        if (fallbackStart !== -1 && fallbackStart >= currentPos) {
          boundaries.push({
            start: fallbackStart,
            end: fallbackStart + tokenText.length,
          });
          currentPos = fallbackStart + tokenText.length;
        } else {
          // If still not found or position is before currentPos, advance currentPos
          // This handles edge cases where token text doesn't match exactly
          currentPos = Math.max(currentPos + 1, currentPos);
        }
      } else {
        boundaries.push({
          start: tokenStart,
          end: tokenStart + tokenText.length,
        });
        currentPos = tokenStart + tokenText.length;
      }
    }

    // Ensure boundaries don't overlap and are in order
    const sortedBoundaries = boundaries
      .filter(b => b.start >= 0 && b.end <= text.length)
      .sort((a, b) => a.start - b.start);

    // Remove any overlapping boundaries
    const cleanedBoundaries: Array<{ start: number; end: number }> = [];
    for (const boundary of sortedBoundaries) {
      if (cleanedBoundaries.length === 0 || boundary.start >= cleanedBoundaries[cleanedBoundaries.length - 1].end) {
        cleanedBoundaries.push(boundary);
      }
    }

    return cleanedBoundaries;
  } catch (error) {
    console.error('Error getting token boundaries:', error);
    return [];
  }
}

/**
 * Split text into sentences
 */
export function splitSentences(text: string): string[] {
  return text
    .split(/(?<=[.!?])\s+/)
    .filter((s) => s.trim().length > 0);
}

/**
 * Split text into paragraphs
 */
export function splitParagraphs(text: string): string[] {
  return text
    .split(/\n\s*\n/)
    .filter((p) => p.trim().length > 0);
}

/**
 * Highlight search terms in text
 */
export function highlightTerms(text: string, terms: string[]): string {
  let result = text;
  terms.forEach((term) => {
    const regex = new RegExp(`(${term})`, 'gi');
    result = result.replace(regex, '<mark>$1</mark>');
  });
  return result;
}

// ============================================
// Color Utilities
// ============================================

/**
 * Convert hex to RGB
 */
export function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : null;
}

/**
 * Generate color from string (deterministic)
 */
export function stringToColor(str: string): string {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  const hue = hash % 360;
  return `hsl(${hue}, 70%, 50%)`;
}

// ============================================
// Validation Utilities
// ============================================

/**
 * Validate email format
 */
export function isValidEmail(email: string): boolean {
  const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return regex.test(email);
}

/**
 * Validate URL format
 */
export function isValidUrl(url: string): boolean {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
}

// ============================================
// Storage Utilities
// ============================================

/**
 * Safe localStorage get with JSON parsing
 */
export function getStorageItem<T>(key: string, defaultValue: T): T {
  if (typeof window === 'undefined') return defaultValue;
  try {
    const item = localStorage.getItem(key);
    return item ? JSON.parse(item) : defaultValue;
  } catch {
    return defaultValue;
  }
}

/**
 * Safe localStorage set with JSON stringify
 */
export function setStorageItem<T>(key: string, value: T): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    console.error('Error saving to localStorage:', error);
  }
}

/**
 * Remove localStorage item
 */
export function removeStorageItem(key: string): void {
  if (typeof window === 'undefined') return;
  localStorage.removeItem(key);
}
